import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { workSchedulesAPI, timeEntriesAPI } from '../../services/api';
import { FiRefreshCw, FiClock, FiChevronLeft, FiChevronRight, FiCalendar, FiFolder, FiExternalLink } from 'react-icons/fi';
import LoadingSpinner from '../common/LoadingSpinner';
import DayDetailsModal from './DayDetailsModal';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const CleaningStaffCalendar = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [schedules, setSchedules] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);

  // Current month/year state
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12

  useEffect(() => {
    loadData();
  }, [currentYear, currentMonth, user]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      // Load schedules and time entries in parallel
      const [schedulesData, timeEntriesData] = await Promise.all([
        workSchedulesAPI.getByMonth(currentYear, currentMonth, {
          employee_id: user?.id
        }),
        timeEntriesAPI.getByMonth(currentYear, currentMonth, {
          user_id: user?.id
        })
      ]);
      setSchedules(schedulesData);
      setTimeEntries(timeEntriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los datos'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
    addNotification({
      type: 'info',
      message: 'Datos actualizados'
    });
  };

  const handlePreviousMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (date, hasData) => {
    if (!hasData) return; // Don't open modal for days without data
    setSelectedDate(date);
    setShowDayDetails(true);
  };

  // Get calendar days for the month
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Calculate days to show from previous month (Monday as first day)
    const daysFromPrevMonth = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Calculate days to show from next month to complete the grid
    const totalCells = Math.ceil((daysInMonth + daysFromPrevMonth) / 7) * 7;
    const daysFromNextMonth = totalCells - daysInMonth - daysFromPrevMonth;

    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        isPrevMonth: true,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        isPrevMonth: false,
        date: new Date(year, month, i)
      });
    }

    // Next month days
    for (let i = 1; i <= daysFromNextMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        isPrevMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  // Get schedule for a specific date
  const getScheduleForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return schedules.find(s => {
      const scheduleDateStr = s.date.split('T')[0];
      return scheduleDateStr === dateStr;
    });
  };

  // Get time entries for a specific date
  const getTimeEntriesForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return timeEntries.filter(entry => {
      if (!entry.clock_in_time) return false;
      const entryDateStr = entry.clock_in_time.split('T')[0];
      return entryDateStr === dateStr;
    });
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const getTotalScheduledHours = () => {
    return schedules.reduce((total, schedule) => total + (schedule.hours || 0), 0);
  };

  const getTotalWorkedHours = () => {
    const totalMinutes = timeEntries.reduce((total, entry) => total + (entry.total_minutes || 0), 0);
    return totalMinutes / 60;
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const formatHoursMinutes = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" text="Cargando datos..." />
      </div>
    );
  }

  const calendarDays = getCalendarDays();
  const totalScheduledHours = getTotalScheduledHours();
  const totalWorkedHours = getTotalWorkedHours();

  return (
    <div className="w-full mx-auto bg-gray-50 min-h-screen pb-4">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="px-4 py-3">
          {/* Title with action buttons */}
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-gray-900">
              Mi Control de Jornada
            </h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleToday}
                className="btn btn-secondary text-xs py-1 px-2"
              >
                Hoy
              </button>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="btn btn-secondary p-1.5"
                aria-label="Actualizar"
              >
                <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Mes anterior"
            >
              <FiChevronLeft className="h-5 w-5 text-gray-600" />
            </button>

            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
            </div>

            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Mes siguiente"
            >
              <FiChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiCalendar className="h-4 w-4 text-blue-600" />
                  <p className="text-xs text-blue-600 font-medium">Horas asignadas</p>
                </div>
                <p className="text-sm font-bold text-blue-900">{formatHours(totalScheduledHours)}</p>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiClock className="h-4 w-4 text-green-600" />
                  <p className="text-xs text-green-600 font-medium">Horas trabajadas</p>
                </div>
                <p className="text-sm font-bold text-green-900">{formatHours(totalWorkedHours)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="px-2 py-4">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Day names header */}
          <div className="grid grid-cols-7 bg-gray-100 border-b">
            {DAY_NAMES.map((dayName, index) => (
              <div
                key={index}
                className="text-center py-2 text-xs font-semibold text-gray-600"
              >
                {dayName}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayInfo, index) => {
              const schedule = getScheduleForDate(dayInfo.date);
              const dayTimeEntries = getTimeEntriesForDate(dayInfo.date);
              const hasSchedule = schedule && schedule.hours > 0;
              const hasTimeEntries = dayTimeEntries.length > 0;
              const today = isToday(dayInfo.date);
              const hasData = hasSchedule || hasTimeEntries;

              // Calculate total worked minutes for the day
              const totalWorkedMinutes = dayTimeEntries.reduce(
                (sum, entry) => sum + (entry.total_minutes || 0),
                0
              );
              const workedHours = totalWorkedMinutes / 60;

              return (
                <div
                  key={index}
                  onClick={() => hasData && handleDayClick(dayInfo.date, hasData)}
                  className={`
                    border-b border-r min-h-24 flex flex-col p-1 overflow-hidden
                    ${!dayInfo.isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
                    ${today ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset' : ''}
                    ${hasData ? 'cursor-pointer hover:bg-gray-50' : ''}
                    transition-colors
                  `}
                >
                  {/* Day number */}
                  <div
                    className={`
                      text-sm font-medium mb-1 text-left
                      ${!dayInfo.isCurrentMonth ? 'text-gray-400' : 'text-gray-900'}
                      ${today ? 'text-blue-700 font-bold' : ''}
                    `}
                  >
                    {dayInfo.day}
                  </div>

                  <div className="flex flex-col w-full space-y-1">
                    {/* Schedule info */}
                    {hasSchedule && (
                      <div className="w-full">
                        <div className="text-[11px] font-bold text-blue-800 bg-blue-100 px-1.5 py-0.5 rounded text-center">
                          {formatHoursMinutes(schedule.hours)}
                        </div>
                      </div>
                    )}

                    {/* Time entries info */}
                    {hasTimeEntries && (
                      <div className="w-full">
                        <div className="text-[11px] font-bold text-green-800 bg-green-100 px-1.5 py-0.5 rounded text-center">
                          {formatHoursMinutes(workedHours)}
                        </div>
                        <div className="text-[9px] text-gray-600 text-center mt-0.5">
                          {dayTimeEntries.length} fichaje{dayTimeEntries.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {/* Comparison indicator - only show if complete */}
                    {hasSchedule && hasTimeEntries && workedHours >= schedule.hours && (
                      <div className="w-full mt-auto">
                        <div className="text-[9px] text-green-600 font-medium text-center bg-green-100 rounded px-1 py-0.5">
                          ✓ Completo
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 bg-white rounded-lg shadow-sm p-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Información</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-blue-50 ring-2 ring-blue-500 rounded flex-shrink-0"></div>
              <span className="text-xs text-gray-600">Día actual</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-[11px] font-bold text-blue-800 bg-blue-100 px-2 py-1 rounded flex-shrink-0">
                8:00
              </div>
              <span className="text-xs text-gray-600">Horas asignadas en cuadrante</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-[11px] font-bold text-green-800 bg-green-100 px-2 py-1 rounded flex-shrink-0">
                8:30
              </div>
              <span className="text-xs text-gray-600">Horas trabajadas (fichajes)</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3 p-2 bg-blue-50 rounded">
            💡 Pulsa sobre un día con datos para ver y editar los fichajes
          </p>
        </div>

        {/* Documents Button */}
        {user?.documents_url && (
          <div className="mt-4">
            <a
              href={user.documents_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full btn btn-primary flex items-center justify-center space-x-2 py-3"
            >
              <FiFolder className="h-5 w-5" />
              <span>Ver mis documentos</span>
              <FiExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        {/* Comparison summary */}
        {totalScheduledHours > 0 && totalWorkedHours > 0 && (
          <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Resumen del mes</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Asignadas</p>
                <p className="text-lg font-bold text-blue-600">{formatHours(totalScheduledHours)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Trabajadas</p>
                <p className="text-lg font-bold text-green-600">{formatHours(totalWorkedHours)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Diferencia</p>
                <p className={`text-lg font-bold ${
                  totalWorkedHours >= totalScheduledHours ? 'text-green-600' : 'text-orange-600'
                }`}>
                  {totalWorkedHours >= totalScheduledHours ? '+' : ''}
                  {formatHours(totalWorkedHours - totalScheduledHours)}
                </p>
              </div>
            </div>
            {totalWorkedHours < totalScheduledHours && (
              <div className="mt-3 p-2 bg-orange-50 rounded border border-orange-200">
                <p className="text-xs text-orange-700">
                  Te faltan {formatHours(totalScheduledHours - totalWorkedHours)} para completar las horas asignadas este mes.
                </p>
              </div>
            )}
            {totalWorkedHours >= totalScheduledHours && (
              <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                <p className="text-xs text-green-700">
                  ¡Has cumplido con las horas asignadas este mes!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Day Details Modal */}
      {selectedDate && (
        <DayDetailsModal
          isOpen={showDayDetails}
          onClose={() => {
            setShowDayDetails(false);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
          timeEntries={getTimeEntriesForDate(selectedDate)}
          schedule={getScheduleForDate(selectedDate)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default CleaningStaffCalendar;
